<template>
  <div>
    <div class="d-flex align-items-center mb-1 flex-wrap">
      <div>
        <b-badge
          variant="light-danger"
          class="mr-1 mb-50 filter-chip d-inline-flex align-items-center"
          :class="{ active: activeFilters.includes('missing') }"
          @click="toggleFilter('missing')"
        >
          <b-form-checkbox
            :checked="activeFilters.includes('missing')"
            style="pointer-events: none"
          />
          Missing ({{ statusCounts.missing }})
        </b-badge>

        <b-badge
          variant="light-warning"
          class="mr-1 mb-50 filter-chip d-inline-flex align-items-center"
          :class="{ active: activeFilters.includes('extra') }"
          @click="toggleFilter('extra')"
        >
          <b-form-checkbox
            :checked="activeFilters.includes('extra')"
            style="pointer-events: none"
          />
          Extra ({{ statusCounts.extra }})
        </b-badge>

        <b-badge
          variant="light-warning"
          class="mr-1 mb-50 filter-chip d-inline-flex align-items-center"
          :class="{ active: activeFilters.includes('p. filled') }"
          @click="toggleFilter('p. filled')"
        >
          <b-form-checkbox
            :checked="activeFilters.includes('p. filled')"
            style="pointer-events: none"
          />
          Partially Filled ({{ statusCounts['p. filled'] }})
        </b-badge>

        <b-badge
          variant="light-success"
          class="mr-1 mb-50 filter-chip d-inline-flex align-items-center"
          :class="{ active: activeFilters.includes('fulfilled') }"
          @click="toggleFilter('fulfilled')"
        >
          <b-form-checkbox
            :checked="activeFilters.includes('fulfilled')"
            style="pointer-events: none"
          />
          Fulfilled ({{ statusCounts.fulfilled }})
        </b-badge>

        <b-badge
          variant="light-info"
          class="mr-1 mb-50 filter-chip d-inline-flex align-items-center"
          :class="{ active: activeFilters.includes('returned') }"
          @click="toggleFilter('returned')"
        >
          <b-form-checkbox
            :checked="activeFilters.includes('returned')"
            style="pointer-events: none"
          />
          Returned ({{ statusCounts.returned }})
        </b-badge>

        <b-badge
          variant="light-primary"
          class="mb-50 filter-chip d-inline-flex align-items-center"
          :class="{ active: activeFilters.length === 0 }"
          @click="activeFilters = []"
        >
          <b-form-checkbox
            :checked="activeFilters.length === 0"
            style="pointer-events: none"
          />
          All ({{ records.length }})
        </b-badge>
      </div>

      <div>
        <b-button
          variant="outline-success"
          :disabled="selectedIds.length === 0 || Boolean(updatingManualStatus)"
          class="mx-1"
          @click="openStatusRemark('Fulfilled')"
        >
          Mark Fulfilled
          <b-spinner
            v-if="updatingManualStatus === 'Fulfilled'"
            label="Small Spinner"
            small
          />
        </b-button>

        <b-button
          variant="outline-info"
          :disabled="selectedIds.length === 0 || Boolean(updatingManualStatus)"
          class="mr-1"
          @click="openStatusRemark('Returned')"
        >
          Mark Returned
          <b-spinner
            v-if="updatingManualStatus === 'Returned'"
            label="Small Spinner"
            small
          />
        </b-button>

        <b-button
          variant="outline-secondary"
          :disabled="selectedIds.length === 0 || Boolean(updatingManualStatus)"
          class="mr-1"
          @click="updateSelectedStatus(null)"
        >
          Clear Manual
          <b-spinner
            v-if="updatingManualStatus === 'Clear'"
            label="Small Spinner"
            small
          />
        </b-button>

        <b-button
          variant="outline-primary"
          :disabled="selectedIds.length === 0 || downloading"
          class="mr-1"
          @click="downloadReport"
        >
          <feather-icon
            v-if="!downloading"
            v-b-tooltip.hover="{boundary:'window'}"
            icon="DownloadIcon"
            title="Download"
          />
          <b-spinner
            v-else
            label="Small Spinner"
            small
          />
        </b-button>

        <b-button
          variant="outline-primary"
          :disabled="selectedIds.length === 0 || sendingEmail"
          class="mr-1"
          @click="sendReportEmail"
        >
          <feather-icon
            v-if="!sendingEmail"
            v-b-tooltip.hover="{boundary:'window'}"
            icon="SendIcon"
            title="Send Email"
          />
          <b-spinner
            v-else
            label="Small Spinner"
            small
          />
        </b-button>
      </div>
    </div>

    <b-table
      :key="activeFilters.join('-')"
      :items="filteredRecords"
      :fields="columns"
      responsive="sm"
      striped
      hover
      class="compact-table1"
      :tbody-tr-class="rowClass"
      show-empty
      empty-text="No records found"
    >
      <template #head(select)>
        <b-form-checkbox
          :checked="isAllSelected"
          @change="toggleSelectAll"
        />
      </template>

      <template #cell(select)="row">
        <b-form-checkbox
          v-model="selectedIds"
          :value="row.item.id"
        />
      </template>

      <template #cell(ordered_qty)="row">
        <span>{{ formatQty(row.item.ordered_qty) }}</span>
        <span v-if="Number(row.item.free_quantity)">
          ({{ formatQty(row.item.quantity) }}  +  {{ formatQty(row.item.free_quantity) }})
        </span>
      </template>

      <template #cell(fulfilled_qty)="row">
        {{ formatQty(row.item.fulfilled_qty) }}
      </template>

      <template #cell(remaining_qty)="row">
        {{ formatQty(row.item.remaining_qty) }}
      </template>

      <template #cell(matches)="row">
        <li
          v-for="(item, index) in row.item.matched_items"
          :key="index"
          style="list-style-type: none;"
        >
          {{ item.credit_note_number }} - {{ formatQty(item.matched_qty) }}/{{ formatQty(item.total_qty) }}
          <span v-if="Number(item.remaining_qty)">(R - {{ formatQty(item.remaining_qty) }})</span>
          <span :class="{ 'text-danger': item.rate_mismatch }">- {{ item.rate }}</span>
        </li>
      </template>

      <template #cell(status)="row">
        <span class="d-inline-flex align-items-center">
          <b-badge :variant="statusVariant(row.item.status)">
            {{ row.item.status.toUpperCase() }}
          </b-badge>
          <feather-icon
            v-if="row.item.manual_status"
            v-b-tooltip.hover="{ boundary: 'window' }"
            icon="Edit2Icon"
            size="14"
            class="ml-50 text-primary"
            title="Manually adjusted"
          />
        </span>
      </template>
    </b-table>

    <b-modal
      v-model="remarkModalShow"
      title="Status remark"
      ok-title="Save"
      :ok-disabled="Boolean(updatingManualStatus)"
      :cancel-disabled="Boolean(updatingManualStatus)"
      @ok="confirmStatusRemark"
    >
      <p>
        Optional remark for items marked {{ pendingManualStatus }}.
      </p>
      <b-form-textarea
        v-model="pendingRemark"
        rows="3"
        placeholder="e.g. Date of return from company"
        :disabled="Boolean(updatingManualStatus)"
      />
    </b-modal>
  </div>
</template>

<script>
import {
  BTable,
  BBadge,
  BFormCheckbox,
  BButton,
  BSpinner,
  BModal,
  BFormTextarea,
  VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'
import download from 'downloadjs'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BTable,
    BBadge,
    BFormCheckbox,
    BButton,
    BSpinner,
    BModal,
    BFormTextarea,
  },
  props: {
    items: { type: Array, required: true },
    supplierReturnId: { type: Number, required: true },
  },
  data() {
    return {
      activeFilters: [],
      selectedIds: [],
      downloading: false,
      sendingEmail: false,
      updatingManualStatus: '',
      remarkModalShow: false,
      pendingManualStatus: '',
      pendingRemark: '',
      columns: [
        { key: 'select', label: '' },
        { key: 'product', label: 'Product', sortable: false },
        { key: 'pack', label: 'Pack', sortable: false },
        { key: 'ordered_qty', label: 'Return Qty', sortable: false },
        { key: 'fulfilled_qty', label: 'Fulfilled Qty', sortable: false },
        { key: 'remaining_qty', label: 'Remaining Qty', sortable: false },
        { key: 'batch', label: 'Batch', sortable: false },
        { key: 'company', label: 'Company', sortable: false },
        { key: 'rate', label: 'Rate', sortable: false },
        { key: 'matches', label: 'Matches', sortable: false },
        { key: 'status', label: 'Status', sortable: false },
        { key: 'manual_status_remark', label: 'Remark', sortable: false },
      ],
    }
  },
  computed: {
    records() {
      const records = (this.items || []).map(item => ({
        ...item,
        status: this.computeStatus(item),
        rowClass: this.computeRowClass(item),
      }))

      return this.sortItems(records)
    },
    statusCounts() {
      const counts = {
        missing: 0,
        extra: 0,
        'p. filled': 0,
        fulfilled: 0,
        returned: 0,
      }

      this.records.forEach(record => {
        if (Object.prototype.hasOwnProperty.call(counts, record.status)) {
          counts[record.status] += 1
        }
      })

      return counts
    },
    filteredRecords() {
      if (!this.activeFilters.length) {
        return this.records
      }
      return this.records.filter(i => this.activeFilters.includes(i.status))
    },
    isAllSelected() {
      return this.filteredRecords.length > 0
        && this.filteredRecords.every(r => this.selectedIds.includes(r.id))
    },
  },
  methods: {
    formatQty(value) {
      if (value === null || value === undefined || value === '') return value
      const num = Number(value)
      if (Number.isNaN(num)) return value
      return String(num)
    },
    statusVariant(status) {
      if (status === 'missing') return 'danger'
      if (status === 'p. filled') return 'warning'
      if (status === 'extra') return 'warning'
      if (status === 'returned') return 'info'
      return 'success'
    },
    computeStatus(item) {
      if (item.manual_status === 'Returned') return 'returned'
      if (item.manual_status === 'Fulfilled') return 'fulfilled'

      const remaining = Number(item.remaining_qty)
      const ordered = Number(item.ordered_qty)
      if (remaining === ordered) return 'missing'
      if (remaining > 0) return 'p. filled'

      const hasExtra = (item.matched_items || []).some(m => Number(m.remaining_qty) > 0)
      if (hasExtra) return 'extra'

      return 'fulfilled'
    },
    computeRowClass(item) {
      if (!item) return ''
      if (item.manual_status) return ''
      if ((item.matched_items || []).some(m => Number(m.remaining_qty) > 0)) return 'table-success'
      if (Number(item.remaining_qty) > 0) return 'table-danger'
      return ''
    },
    rowClass(item) {
      return (item && item.rowClass) || ''
    },
    sortItems(items) {
      const order = {
        missing: 1,
        extra: 2,
        'p. filled': 3,
        fulfilled: 4,
        returned: 5,
      }

      return [...items].sort(
        (a, b) => (order[a.status] || 99) - (order[b.status] || 99),
      )
    },
    toggleFilter(status) {
      const index = this.activeFilters.indexOf(status)

      if (index > -1) {
        this.activeFilters.splice(index, 1)
      } else {
        this.activeFilters.push(status)
      }

      this.selectedIds = []
    },
    toggleSelectAll(isChecked) {
      if (isChecked) {
        this.selectedIds = this.filteredRecords.map(r => r.id)
      } else {
        this.selectedIds = []
      }
    },
    openStatusRemark(manualStatus) {
      this.pendingManualStatus = manualStatus
      this.pendingRemark = ''
      this.remarkModalShow = true
    },
    async confirmStatusRemark(event) {
      event.preventDefault()
      const saved = await this.updateSelectedStatus(this.pendingManualStatus, this.pendingRemark)
      if (saved) {
        this.remarkModalShow = false
      }
    },
    async updateSelectedStatus(manualStatus, manualStatusRemark) {
      try {
        this.updatingManualStatus = manualStatus || 'Clear'
        const response = await axios.post(
          `/returns/supplier_return/${this.supplierReturnId}/update_item_statuses/`,
          {
            item_ids: this.selectedIds,
            manual_status: manualStatus,
            manual_status_remark: manualStatusRemark || '',
          },
        )

        this.selectedIds = []
        this.$emit('items-updated', response.data)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: manualStatus
              ? `Items marked as ${manualStatus}`
              : 'Manual status cleared',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        return true
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.detail || 'Failed to update item status',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
        return false
      } finally {
        this.updatingManualStatus = ''
      }
    },
    async downloadReport() {
      try {
        this.downloading = true
        const response = await axios.post('/returns/download_supplier_return_items_match_report/', {
          supplier_return_id: this.supplierReturnId,
          return_item_ids: this.selectedIds,
        }, {
          responseType: 'blob',
        })

        const contentType = response.headers['content-type']
        download(response.data, `Supplier Return Items Match Report - ${this.supplierReturnId}`, contentType)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.detail || 'Failed to download report',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.downloading = false
      }
    },
    async sendReportEmail() {
      try {
        this.sendingEmail = true
        const response = await axios.post('/returns/email_supplier_return_items_match_report/', {
          supplier_return_id: this.supplierReturnId,
          return_item_ids: this.selectedIds,
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: response?.data?.detail || 'Email Sent',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.$emit('email-sent')
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: error?.response?.data?.detail || 'Failed to send email',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.sendingEmail = false
      }
    },
  },
}
</script>

<style scoped>
.filter-chip {
  cursor: pointer;
}

.filter-chip.active {
  border: 2px solid;
}
</style>
